import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {queryOptions} from '@github-ui/react-query'
import type {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {DevelopmentPickerServerQuery} from '@github-ui/server-persisted-queries'
import type {DevelopmentPickerServerQueryQuery} from '@github-ui/server-persisted-queries/types/DevelopmentPickerServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type DevelopmentPickerQueryRepository = Extract<
  DevelopmentPickerServerQueryQuery['node'],
  {readonly repository: unknown}
>['repository']

export type DevelopmentPickerRepository = Pick<DevelopmentPickerQueryRepository, 'name' | 'nameWithOwner'> & {
  readonly owner: Pick<DevelopmentPickerQueryRepository['owner'], 'login'>
}

export function developmentPickerQueryKey(owner: string, repo: string, number: number) {
  return ['issue-viewer', 'development-picker', {owner, repo, number}] as const
}

// Built at module scope so the cached `queryFn` retains only these arguments, not a component's closure scope.
export function developmentPickerQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister: ReturnType<typeof createServerPersistedQueryPersister>,
) {
  return queryOptions({
    queryKey: developmentPickerQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchDevelopmentPicker(owner, repo, number, signal),
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })
}

export async function fetchDevelopmentPicker(
  owner: string,
  repo: string,
  number: number,
  signal?: AbortSignal,
): Promise<DevelopmentPickerRepository | null> {
  const query = new URLSearchParams({queryId: DevelopmentPickerServerQuery})
  const response = await reactFetchJSON(
    `/${owner}/${repo}/issues/${number}/development_picker.json?${query.toString()}`,
    {
      signal,
    },
  )

  if (!response.ok) throw new Error(`Development picker fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: DevelopmentPickerServerQueryQuery; errors?: unknown[]}
  if (!json.data) throw new Error('Development picker query returned no data')

  return json.data.node && 'repository' in json.data.node ? json.data.node.repository : null
}
